import { useState, useImperativeHandle, forwardRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import TextEditor from "./TextEditor";
import { Button } from "@/components/ui/button";
import Text from "@/components/ui/text";
import { cn } from "@/utils/utils";
import { SavedAttachmentInfo } from "@/entities/attachments/types";
import TextEditorMarkdownDisplay from "./components/TextEditorMarkdownDisplay";

export interface TextEditorWithActionsRef {
  startEditing: () => void;
  forceStartEditing: () => void;
}

interface TextEditorWithActionsProps {
  initialValue?: string;
  onSave?: (value: string) => void;
  onCancel?: () => void;
  placeholder?: string;
  disabled?: boolean;
  showActions?: boolean;
  autoSave?: boolean;
  clearOnSave?: boolean;
  startInEditMode?: boolean;
  customEditButton?: JSX.Element;
  className?: string;
  width?: string;
  onAttachmentsChange?: (attachments: SavedAttachmentInfo[]) => void;
  initialAttachments?: SavedAttachmentInfo[];
  minRows?: number;
  showFocusState?: boolean;
}

const TextEditorWithActions = forwardRef<
  TextEditorWithActionsRef,
  TextEditorWithActionsProps
>(
  (
    {
      initialValue = "",
      onSave,
      onCancel,
      placeholder = "Start typing...",
      disabled = false,
      showActions = false,
      autoSave = true,
      clearOnSave = false,
      startInEditMode = false,
      customEditButton,
      className,
      width = "100%",
      onAttachmentsChange,
      initialAttachments = [],
      minRows,
      showFocusState = false
    },
    ref
  ) => {
    const { t } = useTranslation();
    const [isEditing, setIsEditing] = useState(startInEditMode);
    const [currentValue, setCurrentValue] = useState(initialValue);
    const [editingValue, setEditingValue] = useState(initialValue);

    useEffect(() => {
      setCurrentValue(initialValue);
      setEditingValue(initialValue);
    }, [initialValue]);

    useImperativeHandle(ref, () => ({
      startEditing: () => {
        if (!disabled) {
          setIsEditing(true);
          setEditingValue(currentValue);
        }
      },
      forceStartEditing: () => {
        setIsEditing(true);
        setEditingValue(currentValue);
      }
    }));

    const handleEditClick = () => {
      if (!disabled) {
        setIsEditing(true);
        setEditingValue(currentValue);
      }
    };

    const isSaveDisabled = () => {
      const trimmedValue = editingValue.trim();
      return trimmedValue === "" || trimmedValue === currentValue.trim();
    };

    const handleSave = () => {
      const trimmedValue = editingValue.trim();

      if (isSaveDisabled()) return;

      setCurrentValue(trimmedValue);
      setIsEditing(false);
      onSave?.(trimmedValue);

      if (clearOnSave) {
        setCurrentValue("");
        setEditingValue("");
      }
    };

    const handleCancel = () => {
      setEditingValue(currentValue);
      setIsEditing(false);
      onCancel?.();
    };

    const handleTextEditorBlur = (value: string) => {
      setEditingValue(value);

      // Only auto-save if showActions is false
      if (autoSave && !showActions) {
        const trimmedValue = value.trim();
        setCurrentValue(trimmedValue);
        setIsEditing(false);
        onSave?.(trimmedValue);

        if (clearOnSave) {
          setCurrentValue("");
          setEditingValue("");
        }
      }
    };

    const renderEditButton = () => {
      if (customEditButton) {
        return (
          <div
            onClick={handleEditClick}
            className={cn("cursor-pointer", disabled && "cursor-default")}
          >
            {customEditButton}
          </div>
        );
      }

      return (
        <div
          onClick={handleEditClick}
          className={cn(
            "hover:bg-surface-hover-strong rounded-lg cursor-text w-full p-2 min-h-9 flex items-center",
            !currentValue && "cursor-pointer",
            disabled && "hover:!bg-transparent cursor-default !opacity-100"
          )}
          aria-label={t("clickToEdit")}
        >
          <div className="w-full">
            {currentValue ? (
              <TextEditorMarkdownDisplay content={currentValue} />
            ) : (
              <Text variant="body" className="whitespace-pre-wrap">
                {placeholder}
              </Text>
            )}
          </div>
        </div>
      );
    };

    const renderActionButtons = () => {
      if (!showActions) return null;

      return (
        <div
          className="flex justify-end gap-2 mt-2"
          onMouseDown={(e) => e.preventDefault()}
        >
          <Button
            variant="secondary"
            onMouseDown={(e) => {
              e.preventDefault();
              handleCancel();
            }}
          >
            {t("cancel")}
          </Button>
          <Button
            variant="primary"
            onMouseDown={(e) => {
              e.preventDefault();
              handleSave();
            }}
            disabled={isSaveDisabled()}
          >
            {t("save")}
          </Button>
        </div>
      );
    };

    return (
      <div className={cn("flex flex-col", className)} style={{ width }}>
        {isEditing ? (
          <div className="flex flex-col">
            <TextEditor
              initialContent={editingValue}
              onChange={setEditingValue}
              onBlur={handleTextEditorBlur}
              placeholder={placeholder}
              onAttachmentsChange={onAttachmentsChange}
              initialAttachments={initialAttachments}
              autoFocus={startInEditMode}
              showFocusState={showFocusState}
              minRows={minRows}
            />
            {renderActionButtons()}
          </div>
        ) : (
          renderEditButton()
        )}
      </div>
    );
  }
);

TextEditorWithActions.displayName = "TextEditorWithActions";

export default TextEditorWithActions;
